/**
 * Ecran de validare pentru un item de import (registry-import).
 *
 * Afișează identitatea propusă, alertele, PDF-ul documentului și câmpurile
 * extrase. Permite corecturi, aprobare și respingere.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Descriptions, Alert, Tag, Button, Space, Divider,
  Row, Col, Input, Form, Modal, Spin, message, Tooltip,
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, SaveOutlined,
  WarningOutlined, InfoCircleOutlined, ArrowRightOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { registryImportApi, type ItemWithDetails, type ProgramType } from '../../api/registryImportApi';
import { DOC_TYPE_LABELS, ALERT_CODE_LABELS } from './labels';
import DocumentFieldsPanel from './DocumentFieldsPanel';
import { noisyFields } from './ocrNoise';
import type { RegistryQuestionnaireProgram } from '../../components/registry/RegistryQuestionnaireView';

const ItemReviewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [item, setItem] = useState<ItemWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [approving, setApproving] = useState(false);
  /** programul lotului (DERMATO/HEPATO) — pentru randarea chestionarului */
  const [program, setProgram] = useState<RegistryQuestionnaireProgram | null>(null);
  // corecturile pe documente: câmpurile complete (propunere + editări) și tipul (reclasificare)
  const [docFields, setDocFields] = useState<Record<string, Record<string, unknown>>>({});
  const [docTypes, setDocTypes] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  const [pdfPage, setPdfPage] = useState(1);
  // documentele editate efectiv: doar ele intră în corecturi
  const [editedDocs, setEditedDocs] = useState<Set<string>>(new Set());
  const [reprocessing, setReprocessing] = useState(false);

  useEffect(() => {
    if (id) {
      loadItem();
    }
  }, [id]);

  useEffect(() => {
    // Selectează primul document implicit
    if (item && item.documents.length > 0 && !selectedDocId) {
      setSelectedDocId(item.documents[0].id);
    }
  }, [item]);

  // Programul lotului (pentru etichetele chestionarului); la eroare rămâne null → dedus din secțiuni
  useEffect(() => {
    if (!item?.batchId) return;
    let cancelled = false;
    registryImportApi
      .getBatch(item.batchId)
      .then((batch) => {
        if (cancelled || !batch) return;
        const pt: ProgramType | undefined = batch.programType;
        if (pt === 'DERMATO' || pt === 'HEPATO') setProgram(pt === 'DERMATO' ? 'dermato' : 'hepato');
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [item?.batchId]);

  // Scurtături tastatură
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.key === 's') {
        event.preventDefault();
        void handleSave().catch(() => undefined);
      } else if (event.altKey && event.key === 'a') {
        event.preventDefault();
        handleApprove();
      } else if (event.altKey && event.key === 'n') {
        event.preventDefault();
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [item]);

  const loadItem = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await registryImportApi.getItem(id);
      setItem(data);

      // Valorile curente = propunerea peste care se aplică corecturile salvate
      const fields: Record<string, Record<string, unknown>> = {};
      const types: Record<string, string> = {};
      for (const doc of data.documents) {
        const corrected = data.corrections?.documents?.[doc.id];
        fields[doc.id] = { ...(corrected?.fields ?? doc.proposal?.fields ?? {}) };
        types[doc.id] = corrected?.reclassify ?? doc.docType ?? 'necunoscut';
      }
      setDocFields(fields);
      setDocTypes(types);
      setDirty(false);
      setEditedDocs(new Set(Object.keys(data.corrections?.documents ?? {})));

      const identity = { ...(data.proposal?.identity ?? {}), ...(data.corrections?.identity ?? {}) } as Record<string, unknown>;
      form.setFieldsValue({
        cnp: identity.cnp || '',
        firstName: identity.firstName || '',
        lastName: identity.lastName || '',
        dateOfBirth: identity.dateOfBirth || '',
        address: identity.address || '',
        city: identity.city || '',
        county: identity.county || '',
      });
    } catch (error: any) {
      message.error('Eroare la încărcarea item-ului: ' + (error.message || 'necunoscută'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (silent = false) => {
    if (!item) return;

    setSaving(true);
    try {
      const values = form.getFieldsValue();

      const documents: Record<string, { reclassify?: string; fields: Record<string, unknown> }> = {};
      for (const doc of item.documents) {
        if (!editedDocs.has(doc.id)) continue;
        const reclassified = docTypes[doc.id] && docTypes[doc.id] !== doc.docType ? docTypes[doc.id] : undefined;
        documents[doc.id] = { ...(reclassified ? { reclassify: reclassified } : {}), fields: docFields[doc.id] ?? {} };
      }

      const corrections = {
        documents,
        // doar câmpurile schimbate față de propunere: o corectură explicită rezolvă
        // conflictele de identitate, o valoare neatinsă nu trebuie să le ascundă
        identity: changedIdentity(item.proposal?.identity, values),
      };

      await registryImportApi.patchItem(item.id, corrections);
      if (!silent) message.success('Corecturi salvate; alertele au fost recalculate');
      await loadItem();
    } catch (error: any) {
      message.error('Eroare la salvare: ' + apiError(error));
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!item) return;

    const blockingAlerts = item.alerts.filter((a) => a.severity === 'BLOCKING');
    if (blockingAlerts.length > 0) {
      message.warning('Nu se poate aproba: există alerte blocante');
      return;
    }

    setApproving(true);
    try {
      // corecturile nesalvate intră în bundle-ul aprobat
      if (dirty) await handleSave(true);
      await registryImportApi.approveItem(item.id);
      message.success('Aprobat: pacientul a fost creat/actualizat și fișa de registru completată.');
      await loadItem();
    } catch (error: any) {
      message.error('Eroare la aprobare: ' + apiError(error));
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!item || !rejectReason.trim()) {
      message.warning('Introduceți un motiv pentru respingere');
      return;
    }

    setRejecting(true);
    try {
      await registryImportApi.rejectItem(item.id, rejectReason);
      message.success('Item respins');
      setRejectModalOpen(false);
      setRejectReason('');
      await loadItem();
    } catch (error: any) {
      message.error('Eroare la respingere: ' + apiError(error));
    } finally {
      setRejecting(false);
    }
  };

  const handleNext = async () => {
    if (!item) return;

    try {
      // Încarcă lista de item-uri din același lot pentru a găsi următorul NEEDS_REVIEW
      const items = await registryImportApi.listItems(item.batchId);
      const needsReview = items.filter((i) => i.status === 'NEEDS_REVIEW');
      const currentIndex = needsReview.findIndex((i) => i.id === item.id);

      if (currentIndex !== -1 && currentIndex < needsReview.length - 1) {
        const nextItem = needsReview[currentIndex + 1];
        navigate(`/registry-import/items/${nextItem.id}`);
      } else {
        message.info('Acesta este ultimul item de validat din lot');
      }
    } catch (error: any) {
      message.error('Eroare la navigare: ' + (error.message || 'necunoscută'));
    }
  };

  const selectedDoc = item?.documents.find((d) => d.id === selectedDocId);

  // PDF-ul documentului selectat, încărcat autentificat (blob → object URL)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedDocId) return;
    let url: string | null = null;
    let cancelled = false;
    setPdfUrl(null);
    registryImportApi
      .getDocumentBlobUrl(selectedDocId)
      .then((u) => {
        url = u;
        if (!cancelled) setPdfUrl(u);
      })
      .catch(() => message.error('PDF-ul nu a putut fi încărcat'));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [selectedDocId]);

  // Cât timp documentele se procesează, datele nu sunt complete: fără corecturi/aprobare
  const processing = Boolean(
    item &&
      (item.status === 'QUEUED' ||
        item.status === 'PROCESSING' ||
        item.documents.some((d) => d.status === 'QUEUED' || d.status === 'PROCESSING')),
  );
  useEffect(() => {
    if (!processing) return;
    const timer = setInterval(() => void loadItem(), 3000);
    return () => clearInterval(timer);
  }, [processing]);

  const handleReprocess = async () => {
    if (!item) return;
    setReprocessing(true);
    try {
      await registryImportApi.reprocessItem(item.id);
      message.success('Documentele se reprocesează; corecturile anterioare au fost șterse');
      await loadItem();
    } catch (error: any) {
      message.error('Eroare la reprocesare: ' + apiError(error));
    } finally {
      setReprocessing(false);
    }
  };

  const blockingAlerts = item?.alerts.filter((a) => a.severity === 'BLOCKING') || [];
  const infoAlerts = item?.alerts.filter((a) => a.severity === 'INFO') || [];

  const canApprove = item?.status === 'NEEDS_REVIEW' && !processing && blockingAlerts.length === 0;

  const approveTooltip = blockingAlerts.length > 0
    ? `Nu se poate aproba: ${blockingAlerts.map((a) => ALERT_CODE_LABELS[a.code] || a.code).join(', ')}`
    : 'Aprobă și trimite la serviciul de specialitate (Alt+A)';

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Se încarcă...</p>
      </div>
    );
  }

  if (!item) {
    return (
      <Alert
        type="error"
        message="Item-ul nu a putut fi încărcat"
        showIcon
      />
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <Space align="center">
          <h1 style={{ margin: 0 }}>Verificare pacient: {item.folderName}</h1>
          <Tag color={STATUS_META[item.status]?.color}>{STATUS_META[item.status]?.label ?? item.status}</Tag>
        </Space>
        <Space wrap>
          <Button onClick={() => navigate('/registry-import')}>← Înapoi la loturi</Button>
          <Button
            icon={<SaveOutlined />}
            onClick={() => void handleSave().catch(() => undefined)}
            disabled={processing || item.status === 'APPROVED' || !dirty}
            loading={saving}
          >
            Salvează corecturile
          </Button>
          <Tooltip title={approveTooltip}>
            <Button type="primary" size="large" icon={<CheckCircleOutlined />} onClick={handleApprove} loading={approving} disabled={!canApprove}>
              Aprobă → intră în registru
            </Button>
          </Tooltip>
          <Button icon={<ArrowRightOutlined />} onClick={handleNext}>
            Următorul pacient
          </Button>
        </Space>
      </div>

      {item.status === 'APPROVED' ? (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          message="Pacientul a fost aprobat și se află în registru"
          description={
            item.patientId ? (
              <Button
                type="link"
                style={{ padding: 0 }}
                onClick={() =>
                  // direct în spațiul de lucru al programului lotului (registrul importat)
                  navigate(program ? `/patients/${item.patientId}/${program}` : `/patients/${item.patientId}`)
                }
              >
                Deschide fișa de registru a pacientului →
              </Button>
            ) : null
          }
        />
      ) : item.status === 'NEEDS_REVIEW' && !processing ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Ce ai de făcut"
          description={
            <ol style={{ margin: 0, paddingLeft: 18 }}>
              <li>Ia pe rând fiecare document de mai jos (butoanele cu numele documentelor). Stânga = PDF-ul, dreapta = ce a citit sistemul.</li>
              <li>Compară. Câmpurile cu chenar <b>roșu</b> lipsesc, cele cu chenar <b>portocaliu</b> sunt nesigure: verifică-le în PDF (linkul „p. N” sare la pagină) și corectează direct în căsuță.</li>
              <li>Dacă ai corectat ceva, apasă <b>Salvează corecturile</b>. Când totul e corect, apasă <b>Aprobă</b>: pacientul și datele intră în registru.</li>
            </ol>
          }
        />
      ) : null}

      {processing && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Documentele se procesează (OCR, clasificare, extracție)"
          description="Pagina se reîmprospătează singură. Corecturile și aprobarea sunt disponibile după finalizare."
        />
      )}

      {/* Identitate + Alerte */}
      <Card style={{ marginBottom: 16 }}>
        <Descriptions
          title="Identitate propusă"
          bordered
          column={2}
          size="small"
        >
          <Descriptions.Item label="CNP">
            {item.proposal?.identity.cnp || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Nume complet">
            {item.proposal?.identity.lastName} {item.proposal?.identity.firstName}
          </Descriptions.Item>
          <Descriptions.Item label="Sex">
            {item.proposal?.identity.gender === 'MALE' ? 'Masculin' : item.proposal?.identity.gender === 'FEMALE' ? 'Feminin' : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Data nașterii">
            {item.proposal?.identity.dateOfBirth || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Pacient existent" span={2}>
            {item.proposal?.identity.matchedPatientId ? (
              <Tag color="blue">DA (ID: {item.proposal.identity.matchedPatientId.slice(0, 8)})</Tag>
            ) : (
              <Tag color="green">NU (pacient nou)</Tag>
            )}
          </Descriptions.Item>
        </Descriptions>

        {/* Alerte */}
        {(blockingAlerts.length > 0 || infoAlerts.length > 0) && (
          <div style={{ marginTop: 16 }}>
            <Divider orientation="left">Alerte</Divider>
            <Space direction="vertical" style={{ width: '100%' }}>
              {blockingAlerts.map((alert, idx) => (
                <Alert
                  key={idx}
                  type="error"
                  message={ALERT_CODE_LABELS[alert.code] || alert.code}
                  description={alert.message}
                  icon={<WarningOutlined />}
                  showIcon
                />
              ))}
              {infoAlerts.map((alert, idx) => (
                <Alert
                  key={idx}
                  type="info"
                  message={ALERT_CODE_LABELS[alert.code] || alert.code}
                  description={alert.message}
                  icon={<InfoCircleOutlined />}
                  showIcon
                />
              ))}
            </Space>
          </div>
        )}
      </Card>

      <Card size="small" title={`Documentele pacientului (${item.documents.length})`} style={{ marginBottom: 16 }}>
        <Space wrap>
          {item.documents.map((doc) => {
            const attention = attentionCount(doc, docTypes[doc.id], docFields[doc.id]);
            return (
              <Button
                key={doc.id}
                type={doc.id === selectedDocId ? 'primary' : 'default'}
                onClick={() => {
                  setSelectedDocId(doc.id);
                  setPdfPage(1);
                }}
              >
                {DOC_TYPE_LABELS[docTypes[doc.id] ?? doc.docType ?? 'necunoscut'] ?? docTypes[doc.id]}
                {attention > 0 ? ` ⚠ ${attention}` : ' ✓'}
              </Button>
            );
          })}
        </Space>
        <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>⚠ N = câmpuri de verificat (lipsă / nesigure / citite cu greșeli / completate de Gemini); ✓ = nimic de semnalat</div>
      </Card>

      <Row gutter={16}>
        {/* Stânga: PDF */}
        <Col xs={24} lg={12}>
          <Card
            title={selectedDoc ? `PDF: ${selectedDoc.fileName}` : 'PDF'}
            style={{ marginBottom: 16 }}
          >
            {selectedDoc && (
              <div style={{ height: 600, overflow: 'auto' }}>
                {!pdfUrl ? <Spin /> : <iframe
                  key={`${pdfUrl}-${pdfPage}`}
                  src={`${pdfUrl}#page=${pdfPage}`}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title={selectedDoc.fileName}
                />}
              </div>
            )}
          </Card>
        </Col>

        {/* Dreapta: Câmpuri */}
        <Col xs={24} lg={12}>
          <Card title="Identitate (corectabilă)" size="small" style={{ marginBottom: 16 }}>
            <Form form={form} layout="vertical" onValuesChange={() => setDirty(true)}>
              <Form.Item label="CNP" name="cnp">
                <Input maxLength={13} />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Prenume" name="firstName">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Nume" name="lastName">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Data nașterii" name="dateOfBirth">
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>

              <Form.Item label="Adresă" name="address">
                <Input.TextArea rows={2} />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Oraș" name="city">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Județ" name="county">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>

          <Card
            title={selectedDoc ? `Câmpuri din document: ${DOC_TYPE_LABELS[docTypes[selectedDoc.id]] ?? docTypes[selectedDoc.id]}` : 'Câmpuri din document'}
            extra={dirty ? <Tag color="orange">corecturi nesalvate</Tag> : null}
            style={{ marginBottom: 16 }}
          >
            {selectedDoc ? (
              <DocumentFieldsPanel
                document={selectedDoc}
                docType={docTypes[selectedDoc.id] ?? selectedDoc.docType ?? 'necunoscut'}
                fields={docFields[selectedDoc.id] ?? {}}
                program={program}
                onFieldChange={(field, value) => {
                  setEditedDocs((prev) => new Set(prev).add(selectedDoc.id));
                  setDocFields((prev) => {
                    const next = { ...(prev[selectedDoc.id] ?? {}) };
                    if (value === undefined) delete next[field];
                    else next[field] = value;
                    return { ...prev, [selectedDoc.id]: next };
                  });
                  setDirty(true);
                }}
                onReclassify={(docType) => {
                  setEditedDocs((prev) => new Set(prev).add(selectedDoc.id));
                  setDocTypes((prev) => ({ ...prev, [selectedDoc.id]: docType }));
                  setDirty(true);
                }}
                onShowPage={(page) => setPdfPage(page)}
              />
            ) : (
              <span>Niciun document selectat.</span>
            )}
          </Card>

          {/* Acțiuni secundare */}
          <Card size="small" title="Alte acțiuni">
            <Space wrap>
              <Tooltip title="Reia citirea tuturor documentelor (de ex. după o corectură în sistem). Corecturile salvate se șterg; un pacient aprobat revine „de verificat”, iar la o nouă aprobare datele din registru se actualizează.">
                <Button icon={<ReloadOutlined />} onClick={() => void handleReprocess()} loading={reprocessing} disabled={processing}>
                  Reprocesează documentele
                </Button>
              </Tooltip>
              <Button danger icon={<CloseCircleOutlined />} onClick={() => setRejectModalOpen(true)} disabled={item.status === 'APPROVED'}>
                Respinge pacientul (cu motiv)
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Modal respingere */}
      <Modal
        title="Respinge item"
        open={rejectModalOpen}
        onOk={handleReject}
        onCancel={() => {
          setRejectModalOpen(false);
          setRejectReason('');
        }}
        confirmLoading={rejecting}
        okText="Respinge"
        cancelText="Anulează"
        okButtonProps={{ danger: true }}
      >
        <p>Introduceți motivul respingerii:</p>
        <Input.TextArea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          rows={4}
          placeholder="ex: Documente incomplete, CNP invalid, etc."
        />
      </Modal>
    </div>
  );
};

export default ItemReviewPage;

/** Mesajul de eroare din răspunsul API (Nest: { message }), altfel mesajul axios. */
function apiError(error: any): string {
  const msg = error?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join('; ');
  return msg || error?.message || 'necunoscută';
}

const IDENTITY_KEYS = ['cnp', 'firstName', 'lastName', 'dateOfBirth', 'address', 'city', 'county'] as const;

function changedIdentity(
  proposed: Record<string, unknown> | undefined,
  values: Record<string, string | undefined>,
): Record<string, string> {
  const changed: Record<string, string> = {};
  for (const key of IDENTITY_KEYS) {
    const value = (values[key] ?? '').trim();
    if (value && value !== String(proposed?.[key] ?? '')) changed[key] = value;
  }
  return changed;
}

const STATUS_META: Record<string, { color: string; label: string }> = {
  QUEUED: { color: 'default', label: 'în așteptare' },
  PROCESSING: { color: 'blue', label: 'în procesare' },
  NEEDS_REVIEW: { color: 'orange', label: 'de verificat' },
  APPROVED: { color: 'green', label: 'aprobat — în registru' },
  REJECTED: { color: 'red', label: 'respins' },
  FAILED: { color: 'volcano', label: 'eșuat' },
};

/** Câmpurile care cer atenție: lipsă, din Gemini, cu încredere < 0.7 sau citite cu greșeli. */
function attentionCount(
  doc: ItemWithDetails['documents'][number],
  docType: string | undefined,
  currentFields?: Record<string, unknown>,
): number {
  if (docType === 'necunoscut') return 1;
  const p = doc.proposal;
  if (!p) return doc.status === 'FAILED' ? 1 : 0;
  const flagged = new Set<string>([
    ...(p.missingRequired ?? []),
    ...(p.geminiFields ?? []),
    ...Object.entries(p.fieldConfidence ?? {})
      .filter(([, c]) => c < 0.7)
      .map(([f]) => f),
    ...noisyFields(currentFields ?? p.fields),
  ]);
  return flagged.size;
}
